import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

// Un lettore NFC "a tastiera" scrive il codice come una raffica di tasti
// molto ravvicinati e chiude con Invio. Una persona che digita è molto più
// lenta. Si accetta quindi un valore solo se tutti i caratteri sono arrivati
// a distanza ravvicinata: la digitazione da tastiera viene scartata.
//
// Se con il vostro lettore le letture valide venissero rifiutate (lettore
// lento), alzare MAX_GAP_MS; se invece la digitazione a mano venisse
// accettata, abbassarlo.
const MAX_GAP_MS = 100; // pausa massima ammessa tra due caratteri consecutivi
const MIN_LENGTH = 4; // sotto questa lunghezza non può essere un badge
const MAX_LENGTH = 50; // lo stesso limite del campo numero badge sul server

// Passo "Passa il badge" del modulo di registrazione.
// - Nessun campo di testo visibile: i tasti vengono ascoltati a livello di
//   pagina, così su tablet non compare nemmeno la tastiera a schermo.
// - "Inserisci a mano" mostra il campo per digitare il numero.
export default function BadgeScanStep({ onBadge, onBack, error, busy }) {
  const { t } = useLanguage();
  const [manual, setManual] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [invalidRead, setInvalidRead] = useState(false);

  const bufferRef = useRef('');
  const lastKeyAtRef = useRef(0);
  const onBadgeRef = useRef(onBadge);
  onBadgeRef.current = onBadge;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (manual) return undefined;

    const reset = () => {
      bufferRef.current = '';
      lastKeyAtRef.current = 0;
    };

    const handleKeyDown = (e) => {
      if (busyRef.current) return;
      // Lascia passare le scorciatoie e il Tab (per raggiungere i pulsanti)
      if (e.ctrlKey || e.metaKey || e.altKey || e.key === 'Tab') return;

      const now = Date.now();

      if (e.key === 'Enter') {
        // Invio senza nulla letto: è l'azione normale su un pulsante a fuoco
        if (!bufferRef.current) return;
        e.preventDefault();
        const value = bufferRef.current;
        // Anche l'Invio finale deve arrivare subito dopo l'ultimo carattere
        const endedQuickly = now - lastKeyAtRef.current <= MAX_GAP_MS;
        const valid = endedQuickly && value.length >= MIN_LENGTH && value.length <= MAX_LENGTH;
        reset();
        if (valid) {
          setInvalidRead(false);
          onBadgeRef.current(value);
        } else {
          setInvalidRead(true);
        }
        return;
      }

      if (e.key.length !== 1) return; // ignora frecce, Shift, F-tasti…

      e.preventDefault();
      // Una pausa lunga azzera la sequenza: una persona che digita parte
      // sempre da capo e non raggiunge mai la lunghezza minima in tempo
      if (lastKeyAtRef.current && now - lastKeyAtRef.current > MAX_GAP_MS) {
        bufferRef.current = '';
      }
      bufferRef.current += e.key;
      lastKeyAtRef.current = now;
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [manual]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const value = manualValue.trim();
    if (value) onBadge(value);
  };

  if (manual) {
    return (
      <div>
        <h2>{t('badgeScanTitle')}</h2>
        <p className="modal-subtitle">{t('badgeManualInstruction')}</p>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={handleManualSubmit}>
          <div className="field">
            <input
              type="text"
              className="big-badge-input"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              maxLength={MAX_LENGTH}
              autoComplete="off"
              autoFocus
              disabled={busy}
            />
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-ghost" onClick={onBack} disabled={busy}>{t('back')}</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !manualValue.trim()}>
              {busy ? t('submitting') : t('confirm')}
            </button>
          </div>
        </form>
        <button
          type="button"
          className="doc-link-btn"
          style={{ display: 'block', margin: '14px auto 0' }}
          onClick={() => { setManual(false); setManualValue(''); setInvalidRead(false); }}
        >
          {t('badgeManualBackLink')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <h2>{t('badgeScanTitle')}</h2>
      <p className="modal-subtitle">{t('badgeScanInstruction')}</p>
      {error && <div className="form-error">{error}</div>}
      {!error && invalidRead && <div className="form-error">{t('badgeScanInvalid')}</div>}

      <div
        aria-live="polite"
        style={{
          margin: '24px auto',
          padding: '34px 20px',
          maxWidth: 360,
          border: '2px dashed var(--border-hair)',
          borderRadius: 'var(--radius-card)',
          color: 'var(--muted-text)',
          fontSize: '1.05rem',
        }}
      >
        {busy ? t('submitting') : t('badgeScanWaiting')}
      </div>

      <div className="btn-row" style={{ justifyContent: 'center' }}>
        <button type="button" className="btn btn-ghost" onClick={onBack} disabled={busy}>{t('back')}</button>
      </div>
      <button
        type="button"
        className="doc-link-btn"
        style={{ display: 'block', margin: '14px auto 0' }}
        onClick={() => { setManual(true); setInvalidRead(false); }}
        disabled={busy}
      >
        {t('badgeManualButton')}
      </button>
    </div>
  );
}
